import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { COMPANY_LIST } from "../companies";
import {
  ArrowRight, CalendarClock, Clock, FileText, Plane, Gift, ShieldCheck,
  Users, BookOpen, Leaf, Smartphone, MapPin, Sparkles, Lock,
} from "lucide-react";

const FEATURES = [
  { icon: CalendarClock, title: "Ferie e permessi", desc: "Richiedi ferie, permessi e congedi con approvazione del responsabile e calendario del team." },
  { icon: Clock, title: "Presenze e timbrature", desc: "Timbra entrata, uscita e pause; calcolo automatico di prestazione, permessi e straordinari." },
  { icon: FileText, title: "Buste paga e certificati", desc: "Accedi ai cedolini e genera attestati (servizio, reddito, presenza) in PDF." },
  { icon: Plane, title: "Trasferte e note spese", desc: "Gestisci trasferte e rimborsi con doppio livello di approvazione e ricevute." },
  { icon: Gift, title: "Welfare e benefit", desc: "Convenzioni, benefit personalizzati e beni aziendali sempre sotto controllo." },
  { icon: ShieldCheck, title: "Sicurezza accessi", desc: "Registro accessi, mappa geografica, blocco IP e IP fidati per la massima protezione." },
  { icon: Users, title: "Organigramma e rubrica", desc: "Consulta l'organigramma aziendale e trova subito i contatti dei colleghi." },
  { icon: BookOpen, title: "Guida e novità", desc: "Guida contestuale sempre aggiornata e bacheca con tutte le novità del portale." },
];

const NEWS = [
  { icon: Clock, text: "Presenze con multi-pausa e straordinari autorizzati dal responsabile" },
  { icon: Leaf, text: "Sostenibilità ESG: monitoraggio dei chilometri delle auto aziendali" },
  { icon: MapPin, text: "Suite sicurezza: mappa accessi, blocco automatico IP e IP fidati" },
  { icon: Smartphone, text: "Interfaccia ottimizzata per mobile con scorciatoie di timbratura in Home" },
];

const AnteprimaPage = () => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { const t = setTimeout(() => setMounted(true), 30); return () => clearTimeout(t); }, []);

  return (
    <div className="min-h-screen bg-[#0d1b2f] text-white" data-testid="anteprima-page">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-[#0d1b2f]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-[1180px] mx-auto px-5 h-16 flex items-center justify-between">
          <span className="text-[22px] font-black tracking-tight">Tele<span className="text-[#ff7e00]">borsa</span> <span className="font-light text-[#ff7e00]">HR</span></span>
          <Link to="/seleziona-societa" data-testid="anteprima-login-btn" className="inline-flex items-center gap-2 bg-white text-[#16345c] hover:bg-gray-100 text-[13px] font-bold px-4 py-2 rounded-full transition-colors">
            Accedi al portale <ArrowRight size={15} />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#16345c] via-[#12294a] to-[#0d1b2f]" />
        <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#e30613]/20 blur-[120px]" />
        <div className="absolute -bottom-40 -left-24 w-[380px] h-[380px] rounded-full bg-[#3f66b3]/25 blur-[120px]" />
        <div className={`relative max-w-[1180px] mx-auto px-5 pt-20 pb-24 transition-all duration-700 ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <span className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-widest text-[#ff9a9a] bg-white/5 border border-white/15 rounded-full px-4 py-1.5 mb-7">
            <Lock size={13} /> Anteprima riservata
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] max-w-4xl">
            Il nuovo portale <span className="text-[#ff7e00]">HR</span><br />del Gruppo Teleborsa
          </h1>
          <p className="text-base md:text-lg text-gray-300 mt-7 max-w-2xl leading-relaxed">
            Un'unica piattaforma per gestire ferie, presenze, buste paga, trasferte, welfare e molto altro — con un'interfaccia moderna, sicura e pensata anche per il mobile.
          </p>
          <div className="flex flex-wrap items-center gap-4 mt-10">
            <Link to="/seleziona-societa" data-testid="anteprima-hero-cta" className="inline-flex items-center gap-2 bg-[#e30613] hover:bg-[#c00510] text-white text-[15px] font-bold px-7 py-3.5 rounded-full transition-colors shadow-lg shadow-[#e30613]/30">
              Entra nel portale <ArrowRight size={18} />
            </Link>
            <a href="#funzioni" className="inline-flex items-center gap-2 text-[14px] font-bold text-white/80 hover:text-white border border-white/20 hover:border-white/50 px-6 py-3.5 rounded-full transition-colors">
              Scopri le funzioni
            </a>
          </div>

          <div className="flex items-center gap-5 mt-14">
            <span className="text-[12px] uppercase tracking-widest text-gray-400">Al servizio di</span>
            <div className="flex items-center gap-3">
              {COMPANY_LIST.map((c) => (
                <span key={c.code} className="h-11 px-4 rounded-lg bg-white flex items-center justify-center shadow-sm">
                  <img src={c.logo} alt={c.label} className="max-h-6 max-w-[120px] object-contain" />
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="funzioni" className="bg-[#f0f2f5] text-[#16345c] py-20">
        <div className="max-w-[1180px] mx-auto px-5">
          <div className="max-w-2xl mb-12">
            <span className="text-[12px] font-bold uppercase tracking-widest text-[#e30613]">Tutto in un posto</span>
            <h2 className="text-[26px] sm:text-[32px] font-black mt-2 leading-tight">Le funzioni del portale</h2>
            <p className="text-[15px] text-gray-500 mt-3">Ogni area del rapporto di lavoro, gestita in modo semplice e trasparente.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="anteprima-features">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={f.title} data-testid={`anteprima-feature-${i}`} className="group bg-white border border-gray-200 rounded-2xl p-6 hover:border-[#16345c] hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                  <span className="inline-flex w-12 h-12 rounded-xl bg-[#16345c]/8 text-[#16345c] items-center justify-center mb-5 group-hover:bg-[#e30613] group-hover:text-white transition-colors">
                    <Icon size={22} />
                  </span>
                  <h3 className="text-[16px] font-black leading-tight mb-2">{f.title}</h3>
                  <p className="text-[13px] text-gray-500 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* News / highlights */}
      <section className="bg-[#16345c] text-white py-20">
        <div className="max-w-[1180px] mx-auto px-5 grid lg:grid-cols-[1fr_1.1fr] gap-14 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-widest text-[#ff9a9a]"><Sparkles size={14} /> Novità di questa versione</span>
            <h2 className="text-[26px] sm:text-[32px] font-black mt-3 leading-tight">Più veloce, più sicuro, più mobile</h2>
            <p className="text-[15px] text-gray-300 mt-4 leading-relaxed">
              Abbiamo rinnovato le aree più usate e introdotto nuovi strumenti per dipendenti e ufficio HR.
            </p>
            <Link to="/seleziona-societa" className="inline-flex items-center gap-2 mt-8 text-[14px] font-bold text-white border border-white/25 hover:border-white/60 px-6 py-3 rounded-full transition-colors">
              Provalo ora <ArrowRight size={16} />
            </Link>
          </div>
          <ul className="space-y-4" data-testid="anteprima-news">
            {NEWS.map((n, i) => {
              const Icon = n.icon;
              return (
                <li key={i} className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-xl p-4">
                  <span className="shrink-0 w-10 h-10 rounded-lg bg-[#e30613]/20 text-[#ff9a9a] flex items-center justify-center"><Icon size={18} /></span>
                  <span className="text-[14px] text-gray-200 leading-relaxed pt-1.5">{n.text}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* Footer CTA */}
      <footer className="bg-[#0d1b2f] py-14 border-t border-white/10">
        <div className="max-w-[1180px] mx-auto px-5 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <span className="text-[20px] font-black">Tele<span className="text-[#ff7e00]">borsa</span> <span className="font-light text-[#ff7e00]">HR</span></span>
            <p className="text-[13px] text-gray-400 mt-2 flex items-center gap-2"><Lock size={13} /> Pagina di anteprima riservata · non ancora pubblica</p>
          </div>
          <Link to="/seleziona-societa" className="inline-flex items-center gap-2 bg-white text-[#16345c] hover:bg-gray-100 text-[14px] font-bold px-6 py-3 rounded-full transition-colors">
            Accedi al portale <ArrowRight size={16} />
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default AnteprimaPage;
